import { ReactElement, useEffect } from 'react'
import { Alert, Box, Button, Card, Typography } from '@mui/material'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { QuizTaker } from 'src/v3/components/quiz/quiz-taker'
import { CourseOverviewSkeleton } from 'src/v3/classroom-v2/components/overview-skeleton'
import { QuizResultStep } from 'src/v3/classroom-v2/components/quiz-result-step'
import {
  advanceToQuizQuestion,
  loadQuizAttempt,
  quizAnswerSelected,
  quizResultAcknowledged,
  reviewContentRequested,
  scoreQuizAttempt,
  selectQuizAttemptByCourseID,
  startQuizSession,
  submitQuizAnswer,
} from 'src/v3/classroom-v2/classroom-v2-wizard.slice'

// Wires the presentational QuizTaker to the wizard slice. Every piece of state
// that could affect what the user is allowed to see or do — which question is
// current, what's been answered, whether the attempt is scored — lives in the
// slice, not here. This component only reads the attempt union and dispatches
// facts; it holds no useState at all, which is what keeps a reload or a devtools
// action from putting a user somewhere their session doesn't justify.
export let QuizStep = ({
  courseID,
  userID,
  isStartingRetake,
}: {
  courseID: string
  userID?: string
  isStartingRetake: boolean
}): ReactElement => {
  let dispatch = useAppDispatch()
  let attempt = useAppSelector(selectQuizAttemptByCourseID(courseID))

  // Loading the attempt is derived from being in the quiz phase at all, not from
  // a user action: the phase itself came from the server saying inProgress, so
  // arriving here IS the cue. A null attempt (fresh mount, or wiped by a mode
  // flip) is the only trigger, so this can't loop.
  //
  // Gated on !isStartingRetake because startQuizSession's own pending reducer
  // drops the attempt to null in the SAME dispatch that sets startQuizStatus to
  // 'pending' — so the pre-quiz CTA's session POST and this effect's would
  // otherwise fire in the same tick, both calling the reuse-not-create session
  // endpoint at once. Waiting for that POST to settle means there is only ever
  // one session request in flight; the effect re-fires once isStartingRetake
  // drops back to false with the attempt still null, and picks up the session
  // startQuizSession just opened.
  useEffect(() => {
    if (attempt === null && !isStartingRetake)
      dispatch(loadQuizAttempt(courseID))
  }, [attempt === null, isStartingRetake, courseID])

  if (attempt === null || attempt.kind === 'loading') {
    return <CourseOverviewSkeleton />
  }

  if (attempt.kind === 'failedToLoad') {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto' }}>
        <Typography variant="h1" className="sr-only">
          Course Assessment
        </Typography>
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => dispatch(loadQuizAttempt(courseID))}
            >
              Retry
            </Button>
          }
        >
          We couldn&apos;t load your assessment. Your answers so far are saved —
          retrying will pick up exactly where you left off.
        </Alert>
      </Box>
    )
  }

  if (attempt.kind === 'scored') {
    return (
      <QuizResultStep
        result={attempt.result}
        isStartingRetake={isStartingRetake}
        // A retake is a new session from the server's point of view, so it goes
        // through the same session endpoint the pre-quiz CTA uses. The phase
        // moves because startQuizStatus goes 'pending' (see selectWizardPhase),
        // not because this dispatch resolved — and this component's own effect
        // above re-opens the session once it does, rather than a progress
        // refetch driving it.
        onRetakeQuiz={() => dispatch(startQuizSession(courseID))}
        onReviewContent={() => dispatch(reviewContentRequested(courseID))}
        // The scored attempt is what holds this surface open (see
        // selectWizardPhase), so acknowledging it is what lets the phase fall
        // through to the survey or certificate. No navigation here: the phase is
        // derived from the server's progress record, and this only removes the
        // reason to stay.
        onContinue={() => dispatch(quizResultAcknowledged(courseID))}
      />
    )
  }

  // Everything answered, attempt not scored — the state the questions endpoint
  // signals by returning no isCurrent question. Reachable by closing the tab
  // between the last answer and the result POST, so it needs a real surface
  // rather than being treated as a loading failure.
  if (attempt.kind === 'submitNow') {
    return (
      <Card variant="outlined" sx={{ maxWidth: 640, mx: 'auto', p: 4 }}>
        <Typography variant="h1" sx={{ fontSize: '1.4rem', mb: 1 }}>
          You&apos;ve answered every question
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Submit your assessment to see your score.
        </Typography>
        <Button
          variant="contained"
          disabled={attempt.isAdvancing}
          onClick={() =>
            dispatch(
              scoreQuizAttempt({ courseID, sessionID: attempt.sessionID }),
            )
          }
        >
          Submit Assessment
        </Button>
      </Card>
    )
  }

  let current = attempt.questions[attempt.currentIndex]
  let isLastQuestion = attempt.currentIndex === attempt.questions.length - 1

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto' }}>
      <Typography variant="h1" className="sr-only">
        Course Assessment
      </Typography>
      {attempt.answerError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {attempt.answerError}
        </Alert>
      )}
      <QuizTaker
        question={current.quizQuestion}
        answers={current.questionAnswers}
        questionNumber={attempt.currentIndex + 1}
        totalQuestions={attempt.questions.length}
        selectedAnswerID={attempt.selectedAnswerID}
        feedback={attempt.feedback}
        isSubmitting={attempt.isSubmittingAnswer}
        advanceLabel={isLastQuestion ? 'See Results' : 'Next Question'}
        isAdvancing={attempt.isAdvancing}
        userID={userID}
        onSelectAnswer={(answerID) =>
          dispatch(quizAnswerSelected({ courseID, answerID }))
        }
        onSubmitAnswer={() => {
          // The slice ignores a selection once feedback is in, so this can only
          // ever post a staged, unanswered choice.
          if (!attempt.selectedAnswerID) return
          dispatch(
            submitQuizAnswer({
              courseID,
              sessionID: attempt.sessionID,
              questionID: current.quizQuestion.id,
              answerID: attempt.selectedAnswerID,
            }),
          )
        }}
        onAdvance={() => {
          if (isLastQuestion) {
            dispatch(
              scoreQuizAttempt({ courseID, sessionID: attempt.sessionID }),
            )
            return
          }
          dispatch(
            advanceToQuizQuestion({
              courseID,
              sessionID: attempt.sessionID,
              // Pin the next question BEFORE the local index moves — the slice
              // advances only when this POST succeeds.
              questionID:
                attempt.questions[attempt.currentIndex + 1].quizQuestion.id,
            }),
          )
        }}
      />
    </Box>
  )
}
